import type { TreeGraph } from '@nasabtree/shared-types';
import type { Datum } from 'family-chart';

/**
 * Convert NasabTree's relational TreeGraph into family-chart's flat Datum[] format.
 * Maps our fields to family-chart's expected field names.
 */
export function treeGraphToDatum(graph: TreeGraph, treeId: string): Datum[] {
  const apiBase = import.meta.env.VITE_API_URL || '';

  // Build parent/child indexes from relationships
  const parentsByChild = new Map<string, string[]>();
  const childrenByParent = new Map<string, string[]>();

  for (const rel of graph.relationships) {
    const parents = parentsByChild.get(rel.child_id) ?? [];
    parents.push(rel.parent_id);
    parentsByChild.set(rel.child_id, parents);

    const children = childrenByParent.get(rel.parent_id) ?? [];
    children.push(rel.child_id);
    childrenByParent.set(rel.parent_id, children);
  }

  // Build spouse relationships from marriages, sorted by wife_order for polygamy
  const marriagesByHusband = new Map<string, typeof graph.marriages>();
  for (const m of graph.marriages) {
    const list = marriagesByHusband.get(m.husband_id) ?? [];
    list.push(m);
    marriagesByHusband.set(m.husband_id, list);
  }
  for (const [, list] of marriagesByHusband) {
    list.sort((a, b) => (a.wife_order ?? 1) - (b.wife_order ?? 1));
  }

  const spousesByPerson = new Map<string, string[]>();
  for (const m of graph.marriages) {
    if (!spousesByPerson.has(m.husband_id)) {
      const sorted = (marriagesByHusband.get(m.husband_id) ?? []).map((mm) => mm.wife_id);
      spousesByPerson.set(m.husband_id, sorted);
    }
    const wSpouses = spousesByPerson.get(m.wife_id) ?? [];
    if (!wSpouses.includes(m.husband_id)) wSpouses.push(m.husband_id);
    spousesByPerson.set(m.wife_id, wSpouses);
  }

  // Deduplicate children lists
  for (const [key, list] of childrenByParent) {
    childrenByParent.set(key, [...new Set(list)]);
  }

  return graph.persons.map((person): Datum => {
    const name = person.ism_ar || person.ism_en || '---';
    const familyName = person.family_name ? ` ${person.family_name}` : '';
    const label = `${name}${familyName}`;

    const avatar = person.photo_r2_key
      ? `${apiBase}/api/trees/${treeId}/persons/${person.id}/photo`
      : undefined;

    return {
      id: person.id,
      data: {
        gender: person.gender === 'male' ? 'M' : 'F',
        'first name': name,
        'last name': person.family_name ?? '',
        label,
        avatar,
      },
      rels: {
        parents: parentsByChild.get(person.id) ?? [],
        spouses: spousesByPerson.get(person.id) ?? [],
        children: childrenByParent.get(person.id) ?? [],
      },
    };
  });
}

/**
 * Find the best root ancestor to use as the initial main person.
 */
export function findRootAncestor(data: Datum[]): string | null {
  const roots = data.filter((d) => d.rels.parents.length === 0);
  const patriarch = roots.find((d) => d.data.gender === 'M' && d.rels.children.length > 0);
  if (patriarch) return patriarch.id;
  const maleRoot = roots.find((d) => d.data.gender === 'M');
  if (maleRoot) return maleRoot.id;
  return roots[0]?.id ?? data[0]?.id ?? null;
}
